import { reaction, action, observable, computed, makeObservable } from 'mobx';

import AgentPoll from '../common/agent';

const Agent = AgentPoll.FaqAgent;

const ITEM_LABEL = 'Faq';
const CREATE_SUCCESS = `New ${ITEM_LABEL} added`;
const CREATE_ERROR = `Error creating ${ITEM_LABEL}`;
const REORDER_ERROR = `Error reordering ${ITEM_LABEL}`;
const UPDATE_SUCCESS = 'Saved';
const UPDATE_ERROR = `Error saving ${ITEM_LABEL}`;
const DELETE_SUCCESS = 'Faq removed';
const DELETE_ERROR = `Error deleting ${ITEM_LABEL}`;
// const RESOURCE_DELETE_ERROR = 'Error deleting resource';

export default class FaqStore {
  _loading = false;

  _initialized = false;

  _error = null;

  _items = [];

  constructor(rootStore, projectStore) {
    makeObservable(this, {
      _items: observable,
      _loading: observable,
      _initialized: observable,
      _error: observable,

      resetFlags: action,

      faq: computed,
      isLoading: computed,
      isInitialized: computed,
      error: computed,

      loadData: action,
      toggleOpened: action,
    });

    this.rootStore = rootStore;
    this.projectStore = projectStore;
    reaction(
      () => this.projectStore.isInitialized,
      () => this.loadData(this.projectStore.project.faq, this.projectStore.projectDefault.faq),
    );
  }

  get isLoading() {
    return this._loading;
  }

  get isInitialized() {
    return this._initialized;
  }

  get error() {
    return this._error;
  }

  get faq() {
    return this._items;
  }

  /**
   * Load and prepare data already loaded in ProjectStore
   * @param items
   */
  loadData(faqs, faqDefault) {
    const tmp = [];
    faqs.map((faq) => {
      const defaultFaq = faqDefault.find((faqDef) => faqDef._id === faq._id);
      tmp.push({
        data: faq,
        defaultData: defaultFaq,
        formState: { isOpened: false },
      });
      return faq;
    });
    this._items = tmp;
    this._initialized = true;
    // console.log(this._team);
  }

  /**
   * Create EMPTY decision  POST /project/:id/faq
   * @param projectId
   * @param data
   * @returns {Promise<T>}
   */
  create = (projectId, data) => {
    // console.log('CREATE STORE');
    this._loading = true;
    this._error = null;
    return Agent.create(this.projectStore.project._id)
      .then((res) => {
        // console.log('New member', res.data._id);
        this.rootStore.toastrStore.success(CREATE_SUCCESS);
        this.refreshAndKeepItemOpen(res.data._id);
      })
      .catch((err) => {
        this._error = err;
        this.rootStore.toastrStore.error(CREATE_ERROR, null, err);
      })
      .finally(this.resetFlags);
  };

  async reorder(data) {
    this._loading = false;
    this._error = null;

    try {
      await Agent.reorder(
        this.projectStore.project._id,
        data.map((item) => item.data._id),
      );
      await this.projectStore.refresh();
    } catch (err) {
      this._error = err;
      this.rootStore.toastrStore.error(REORDER_ERROR, null, err);
    } finally {
      this.resetFlags();
    }
  }

  // Update FAQ
  update(itemId, data) {
    // console.log('UPDATE FAQ STORE', data);
    this._loading = true;
    this._error = null;
    return Agent.update(this.projectStore.project._id, itemId, data)
      .then(() => {
        // console.log('New member', res.data._id);
        this.rootStore.toastrStore.success(UPDATE_SUCCESS);
        this.refreshAndKeepItemOpen(itemId);
      })
      .catch((err) => {
        this._error = err;
        this.rootStore.toastrStore.error(UPDATE_ERROR, null, err);
      })
      .finally(this.resetFlags);
  }

  /**
   * Remove decision DELETE /project/:id/faq/:faqId
   * @param itemId
   * @returns {Promise<T>}
   */
  remove = (itemId) => {
    // console.log('DELETE STORE');
    this._loading = true;
    this._error = null;
    return Agent.remove(this.projectStore.project._id, itemId)
      .then(() => {
        this.rootStore.toastrStore.success(DELETE_SUCCESS);
        return this.projectStore.refresh();
      })
      .catch((err) => {
        this._error = err;
        this.rootStore.toastrStore.error(DELETE_ERROR, null, err);
      })
      .finally(this.resetFlags);
  };

  /**
   * Toggle items/form open/close status
   * @param faqIds
   */
  toggleOpened = (faqIds) => {
    faqIds.forEach((id) => {
      const fq = this.faq.find((f) => f.data._id === id);
      if (fq) {
        fq.formState.isOpened = !fq.formState.isOpened;
      }
    });
  };

  async refreshAndKeepItemOpen(itemId) {
    const openedFaqsIds = this.faq
      .filter((f) => f.formState.isOpened && f.data._id !== itemId)
      .map((fq) => fq.data._id);

    this.projectStore.refresh().then(() => this.toggleOpened([...openedFaqsIds, itemId]));
  }

  resetFlags = () => {
    this._loading = false;
  };
}
